import React, { useEffect, useState } from "react";
import { api, fmtUSD, fmtNumber } from "../lib/api";
import KpiCard from "../components/KpiCard";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  CheckCircle2,
  Clock,
  Hourglass,
  Receipt,
  Coins,
  Layers,
  Database,
  FileText,
  Cpu,
} from "lucide-react";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
} from "recharts";

const CLOUD_COLORS = {
  AWS: "#ff9900",
  Azure: "#0078d4",
  GCP: "#00579e",
  OCI: "#c74634",
};

const STATUS_TONE = {
  Done: { bg: "#dcfce7", fg: "#15803d", icon: CheckCircle2 },
  "In Progress": { bg: "#fef3c7", fg: "#a16207", icon: Hourglass },
  Pending: { bg: "#f3f4f6", fg: "#52525b", icon: Clock },
  Reconciled: { bg: "#dcfce7", fg: "#15803d" },
  Synced: { bg: "#dcfce7", fg: "#15803d" },
  "Pending Sync": { bg: "#fef3c7", fg: "#a16207" },
  Fresh: { bg: "#dcfce7", fg: "#15803d" },
  Stale: { bg: "#fee2e2", fg: "#b91c1c" },
};

function StatusPill({ value, testId }) {
  const t = STATUS_TONE[value] || { bg: "#f3f4f6", fg: "#52525b" };
  return (
    <span
      className="cs-pill"
      style={{ background: t.bg, color: t.fg }}
      data-testid={testId}
    >
      {value}
    </span>
  );
}

export default function Chargeback() {
  const [overview, setOverview] = useState(null);
  const [invoices, setInvoices] = useState(null);
  const [adjustments, setAdjustments] = useState([]);
  const [final, setFinal] = useState([]);
  const [views, setViews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.chargeback.overview(),
      api.chargeback.invoices(),
      api.chargeback.adjustments(),
      api.chargeback.final(),
      api.chargeback.athenaViews(),
    ])
      .then(([o, i, a, f, v]) => {
        if (cancelled) return;
        setOverview(o);
        setInvoices(i);
        setAdjustments(a);
        setFinal(f);
        setViews(v);
      })
      .catch((e) => console.error(e))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading || !overview) {
    return (
      <div className="px-6 lg:px-10 py-10" data-testid="chargeback-loading">
        <div className="cs-card p-10 text-center text-muted-foreground">
          Loading chargeback automation…
        </div>
      </div>
    );
  }

  const t = overview.totals;
  const cloudSplit = [
    { name: "AWS", value: t.by_cloud.AWS, color: CLOUD_COLORS.AWS },
    { name: "Azure", value: t.by_cloud.Azure, color: CLOUD_COLORS.Azure },
    { name: "GCP", value: t.by_cloud.GCP, color: CLOUD_COLORS.GCP },
    { name: "OCI", value: t.by_cloud.OCI, color: CLOUD_COLORS.OCI },
  ];

  return (
    <div className="px-6 lg:px-10 py-8 space-y-6" data-testid="chargeback-page">
      {/* Billing month banner */}
      <div className="cs-card p-5 flex items-center justify-between gap-3 flex-wrap" data-testid="cb-billing-month">
        <div className="flex items-center gap-3">
          <Receipt className="w-5 h-5 text-[#00579e]" />
          <div>
            <h2 className="cs-heading font-semibold text-base tracking-tight">
              Billing Month —{" "}
              <span className="cs-mono text-[#00579e]">{overview.billing_month}</span>
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Chargeback cycle covers AWS, Azure, GCP & Oracle Cloud. Workflow tracked in real time.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="cs-pill bg-[#fef3c7] text-[#a16207]">In Validation</span>
          <span className="text-[11px] text-muted-foreground cs-mono">
            {t.automation_rate_pct}% automated
          </span>
        </div>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <KpiCard
          testId="cb-kpi-billed"
          label="Total Billed"
          value={fmtUSD(t.total_billed_usd, { compact: true })}
          sublabel="across AWS · Azure · GCP · OCI"
          icon={Coins}
          tone="primary"
        />
        <KpiCard
          testId="cb-kpi-chargedback"
          label="Charged Back"
          value={fmtUSD(t.total_chargedback_usd, { compact: true })}
          sublabel={`${t.cost_centers} cost centers`}
          icon={Receipt}
          tone="success"
        />
        <KpiCard
          testId="cb-kpi-adjustments"
          label="Adjustments"
          value={fmtUSD(t.total_adjustments_usd, { compact: true })}
          sublabel={`${t.pending_adjustments} pending sync`}
          icon={FileText}
          tone={t.pending_adjustments ? "warning" : "teal"}
        />
        <KpiCard
          testId="cb-kpi-resources"
          label="Resources Mapped"
          value={fmtNumber(t.resources_mapped)}
          sublabel="accounts · subs · projects"
          icon={Layers}
          tone="teal"
        />
        <KpiCard
          testId="cb-kpi-automation"
          label="Automation Rate"
          value={`${t.automation_rate_pct}%`}
          sublabel="vs. legacy Excel process"
          icon={Cpu}
          tone="primary"
        />
      </div>

      {/* Tabs */}
      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList
          className="bg-white border border-border rounded-full p-1 h-auto inline-flex"
          data-testid="cb-tabs"
        >
          <TabsTrigger value="overview" data-testid="cb-tab-overview" className="rounded-full px-4 h-8 text-xs">
            Overview
          </TabsTrigger>
          <TabsTrigger value="invoices" data-testid="cb-tab-invoices" className="rounded-full px-4 h-8 text-xs">
            Invoices
          </TabsTrigger>
          <TabsTrigger value="adjustments" data-testid="cb-tab-adjustments" className="rounded-full px-4 h-8 text-xs">
            Adjustments
          </TabsTrigger>
          <TabsTrigger value="chargebacks" data-testid="cb-tab-chargebacks" className="rounded-full px-4 h-8 text-xs">
            Chargebacks
          </TabsTrigger>
          <TabsTrigger value="views" data-testid="cb-tab-views" className="rounded-full px-4 h-8 text-xs">
            Athena Views
          </TabsTrigger>
        </TabsList>

        {/* OVERVIEW */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="cs-card p-6 lg:col-span-2" data-testid="cb-workflow-card">
              <h3 className="cs-heading text-base font-semibold tracking-tight">
                Monthly Chargeback Workflow
              </h3>
              <p className="text-xs text-muted-foreground mt-0.5 mb-4">
                Pending → Active pipeline for the {overview.billing_month} cycle
              </p>
              <div className="space-y-2">
                {overview.workflow.map((s) => {
                  const tone = STATUS_TONE[s.status] || {};
                  const Icon = tone.icon || Clock;
                  return (
                    <div
                      key={s.step}
                      className="flex items-center gap-3 py-2.5 border-b border-border last:border-0"
                      data-testid={`cb-workflow-step-${s.step}`}
                    >
                      <div
                        className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold cs-mono"
                        style={{ background: tone.bg || "#f3f4f6", color: tone.fg || "#52525b" }}
                      >
                        {s.step}
                      </div>
                      <span className="flex-1 text-sm font-medium">{s.name}</span>
                      <Icon className="w-3.5 h-3.5" style={{ color: tone.fg || "#52525b" }} />
                      <StatusPill value={s.status} />
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="cs-card p-6" data-testid="cb-cloud-split-card">
              <h3 className="cs-heading text-base font-semibold tracking-tight">
                Billed by Cloud
              </h3>
              <p className="text-xs text-muted-foreground mt-0.5 mb-3">
                Raw billing share before chargeback adjustments
              </p>
              <div className="h-48">
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={cloudSplit}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={42}
                      outerRadius={72}
                      paddingAngle={2}
                    >
                      {cloudSplit.map((d, i) => (
                        <Cell key={i} fill={d.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{ borderRadius: 4, fontSize: 12 }}
                      formatter={(v) => fmtUSD(v, { compact: true })}
                    />
                    <Legend wrapperStyle={{ fontSize: 11 }} iconSize={10} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-1.5 mt-1">
                {cloudSplit.map((c) => (
                  <div key={c.name} className="flex items-center gap-2 text-xs" data-testid={`cb-cloud-${c.name.toLowerCase()}`}>
                    <span className="w-2 h-2 rounded-sm" style={{ background: c.color }} />
                    <span className="text-muted-foreground flex-1">{c.name}</span>
                    <span className="cs-mono font-medium">{fmtUSD(c.value, { compact: true })}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </TabsContent>

        {/* INVOICES */}
        <TabsContent value="invoices">
          <div className="cs-card p-0 overflow-hidden" data-testid="cb-invoices-card">
            <div className="p-6 pb-3 border-b border-border">
              <h3 className="cs-heading text-base font-semibold tracking-tight">
                AWS Invoice Segregation
              </h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                Automation mirrors the legacy 4-invoice Excel split — Usage, Subscription, Refund,
                Marketplace
              </p>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-[hsl(var(--cs-tint))] border-b border-border">
                <tr className="text-left">
                  <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground">
                    Invoice ID
                  </th>
                  <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground">
                    Type
                  </th>
                  <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground text-right">
                    Amount
                  </th>
                  <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {invoices.aws_invoices.map((inv) => (
                  <tr
                    key={inv.invoice_id}
                    className="cs-row-hover border-b border-border last:border-0"
                    data-testid={`cb-invoice-${inv.invoice_id}`}
                  >
                    <td className="px-4 py-2.5 cs-mono">{inv.invoice_id}</td>
                    <td className="px-4 py-2.5 font-medium">{inv.type}</td>
                    <td
                      className={`px-4 py-2.5 text-right cs-mono font-semibold ${
                        inv.amount_usd < 0 ? "text-[#15803d]" : "text-[#00579e]"
                      }`}
                    >
                      {fmtUSD(inv.amount_usd, { compact: true })}
                    </td>
                    <td className="px-4 py-2.5">
                      <StatusPill value={inv.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
            <div className="cs-card p-6" data-testid="cb-azure-summary">
              <div className="flex items-center justify-between">
                <h3 className="cs-heading text-base font-semibold tracking-tight">Azure</h3>
                <span className="cs-pill" style={{ background: "#dbeafe", color: "#0078d4" }}>
                  Billing export
                </span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Single billing export feed — split by subscription downstream.
              </p>
              <div className="cs-mono text-2xl font-semibold text-[#0078d4] mt-4">
                {fmtUSD(invoices.azure_total_usd, { compact: true })}
              </div>
              <div className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground mt-1">
                month total
              </div>
            </div>

            <div className="cs-card p-6" data-testid="cb-gcp-summary">
              <div className="flex items-center justify-between">
                <h3 className="cs-heading text-base font-semibold tracking-tight">GCP</h3>
                <span className="cs-pill" style={{ background: "#dbeafe", color: "#00579e" }}>
                  Billing export
                </span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                BigQuery billing export — split by project downstream.
              </p>
              <div className="cs-mono text-2xl font-semibold text-[#00579e] mt-4">
                {fmtUSD(invoices.gcp_total_usd, { compact: true })}
              </div>
              <div className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground mt-1">
                month total
              </div>
            </div>
          </div>
        </TabsContent>

        {/* ADJUSTMENTS */}
        <TabsContent value="adjustments">
          <div className="cs-card p-0 overflow-hidden" data-testid="cb-adjustments-card">
            <div className="p-6 pb-3 border-b border-border flex items-center justify-between flex-wrap gap-3">
              <div>
                <h3 className="cs-heading text-base font-semibold tracking-tight">
                  Manual Adjustments
                </h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  MAP credits, outposts, manual overrides & SPAUF allocations — synced into Athena
                  views by CAMExport
                </p>
              </div>
              <span className="text-[11px] cs-mono text-muted-foreground">
                {adjustments.length} entries
              </span>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-[hsl(var(--cs-tint))] border-b border-border">
                <tr className="text-left">
                  <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground">ID</th>
                  <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground">Cloud</th>
                  <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground">Resource</th>
                  <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground">Type</th>
                  <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground text-right">Amount</th>
                  <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground">Comment</th>
                  <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground">Status</th>
                </tr>
              </thead>
              <tbody>
                {adjustments.map((a) => (
                  <tr
                    key={a.id}
                    className="cs-row-hover border-b border-border last:border-0"
                    data-testid={`cb-adj-row-${a.id}`}
                  >
                    <td className="px-4 py-2.5 cs-mono text-xs">{a.id}</td>
                    <td className="px-4 py-2.5">
                      <span
                        className="cs-pill"
                        style={{ background: `${CLOUD_COLORS[a.cloud]}1a`, color: CLOUD_COLORS[a.cloud] }}
                      >
                        {a.cloud}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 cs-mono text-xs text-muted-foreground">{a.resource_id}</td>
                    <td className="px-4 py-2.5 text-xs font-medium">{a.adjustment_type}</td>
                    <td
                      className={`px-4 py-2.5 text-right cs-mono ${
                        a.amount_usd < 0 ? "text-[#15803d]" : "text-[#be185d]"
                      }`}
                    >
                      {a.amount_usd < 0 ? "-" : "+"}
                      {fmtUSD(Math.abs(a.amount_usd), { compact: true })}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-muted-foreground max-w-xs truncate">
                      {a.comments}
                    </td>
                    <td className="px-4 py-2.5">
                      <StatusPill value={a.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* CHARGEBACKS — final distribution */}
        <TabsContent value="chargebacks">
          <div className="cs-card p-0 overflow-hidden" data-testid="cb-final-card">
            <div className="p-6 pb-3 border-b border-border">
              <h3 className="cs-heading text-base font-semibold tracking-tight">
                Final Chargebacks by Cost Center
              </h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                Aggregated charges per Cost Center · GL ID · WBS · Business Unit (post-adjustments)
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[hsl(var(--cs-tint))] border-b border-border">
                  <tr className="text-left">
                    <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground">CC</th>
                    <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground">BU L2 / L3</th>
                    <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground">App</th>
                    <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground">GL · WBS</th>
                    <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground text-right">AWS</th>
                    <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground text-right">Azure</th>
                    <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground text-right">GCP</th>
                    <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground text-right">OCI</th>
                    <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {final.map((f) => (
                    <tr
                      key={f.cost_center}
                      className="cs-row-hover border-b border-border last:border-0"
                      data-testid={`cb-final-row-${f.cost_center}`}
                    >
                      <td className="px-4 py-2.5 cs-mono font-semibold text-[#00579e]">
                        {f.cost_center}
                      </td>
                      <td className="px-4 py-2.5 text-xs">
                        <div className="font-medium">{f.bu_l2}</div>
                        <div className="text-muted-foreground">{f.bu_l3}</div>
                      </td>
                      <td className="px-4 py-2.5 text-xs">{f.application}</td>
                      <td className="px-4 py-2.5 text-xs cs-mono text-muted-foreground">
                        <div>{f.gl_id}</div>
                        <div>{f.wbs_id}</div>
                      </td>
                      <td className="px-4 py-2.5 text-right cs-mono text-xs" style={{ color: CLOUD_COLORS.AWS }}>
                        {f.aws_usd ? fmtUSD(f.aws_usd, { compact: true }) : "—"}
                      </td>
                      <td className="px-4 py-2.5 text-right cs-mono text-xs" style={{ color: CLOUD_COLORS.Azure }}>
                        {f.azure_usd ? fmtUSD(f.azure_usd, { compact: true }) : "—"}
                      </td>
                      <td className="px-4 py-2.5 text-right cs-mono text-xs" style={{ color: CLOUD_COLORS.GCP }}>
                        {f.gcp_usd ? fmtUSD(f.gcp_usd, { compact: true }) : "—"}
                      </td>
                      <td className="px-4 py-2.5 text-right cs-mono text-xs" style={{ color: CLOUD_COLORS.OCI }}>
                        {f.oci_usd ? fmtUSD(f.oci_usd, { compact: true }) : "—"}
                      </td>
                      <td className="px-4 py-2.5 text-right cs-mono font-semibold text-[#00579e]">
                        {fmtUSD(f.amount_usd, { compact: true })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* ATHENA VIEWS */}
        <TabsContent value="views">
          <div className="cs-card p-0 overflow-hidden" data-testid="cb-views-card">
            <div className="p-6 pb-3 border-b border-border">
              <h3 className="cs-heading text-base font-semibold tracking-tight">
                Athena Views — Freshness Monitor
              </h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                Per-cloud view inventory · row counts · last refresh · refresh status
              </p>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-[hsl(var(--cs-tint))] border-b border-border">
                <tr className="text-left">
                  <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground">Cloud</th>
                  <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground">View Name</th>
                  <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground text-right">Rows</th>
                  <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground text-right">Size</th>
                  <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground text-right">Last Refresh</th>
                  <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground">Status</th>
                </tr>
              </thead>
              <tbody>
                {views.map((v) => (
                  <tr
                    key={v.cloud + v.view_name}
                    className="cs-row-hover border-b border-border last:border-0"
                    data-testid={`cb-view-${v.view_name}`}
                  >
                    <td className="px-4 py-2.5">
                      <span
                        className="cs-pill"
                        style={{ background: `${CLOUD_COLORS[v.cloud]}1a`, color: CLOUD_COLORS[v.cloud] }}
                      >
                        {v.cloud}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 cs-mono text-xs">{v.view_name}</td>
                    <td className="px-4 py-2.5 text-right cs-mono text-xs">{fmtNumber(v.row_count)}</td>
                    <td className="px-4 py-2.5 text-right cs-mono text-xs text-muted-foreground">{v.size_mb} MB</td>
                    <td className="px-4 py-2.5 text-right cs-mono text-xs text-muted-foreground">
                      {v.last_refresh_minutes_ago}m ago
                    </td>
                    <td className="px-4 py-2.5">
                      <StatusPill value={v.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="cs-card p-5 mt-6 flex items-start gap-3" data-testid="cb-views-help">
            <Database className="w-4 h-4 text-[#00579e] mt-0.5 shrink-0" />
            <div className="text-xs leading-relaxed">
              <div className="font-semibold text-foreground">
                How the views are organised
              </div>
              <p className="text-muted-foreground mt-1">
                AWS views power the full 4-invoice split (Usage · Subscription · Refund · Marketplace)
                plus SP/RI and Outpost flows. Azure and GCP each use 5 views that map the raw billing
                export through a working set into the final chargeback table.
              </p>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
